import { useQuery } from '@apollo/client'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import { FormControlLabel, Switch, Typography } from '@material-ui/core'
import { FirmProjectParametersQuery } from 'api/graphQL/queries/firm.queries'
import { setBPStagedConfig } from 'api/graphQL/reactiveVars/workspaceToggles'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import { isOMBestPracticeFeatureEnabled } from 'shared/utils/bestPracticeUtils'
import { BP_MODE } from '../constants'
import { useBestPracticeManagementPanelStyles } from '../styles/useBestPracticeManagementPanelStyles'
import {
    BPMode,
    emptyParameterSelected,
    emptyParameterToggles,
    parameterConfig,
    ParameterKey,
    ParameterSelectedState,
    ParameterToggleState,
} from '../types/bpManagement.types'
import {
    buildFirmBPParameters,
    FirmDataShape,
} from '../utils/firmBPParameterUtils'
import { BestPracticeTipsList } from './BestPracticeTipsList'
import { ParameterListSection } from './ParameterListSection'

const buildEffectiveSelected = (
    parameterToggles: ParameterToggleState,
    parameterSelected: ParameterSelectedState
): ParameterSelectedState =>
    parameterConfig.reduce<ParameterSelectedState>(
        (acc, { key }) => {
            acc[key] = parameterToggles[key] ? parameterSelected[key] : []
            return acc
        },
        { ...emptyParameterSelected }
    )

export const BestPracticeManagementPanelContent = () => {
    const classes = useBestPracticeManagementPanelStyles()
    const { currentUserActiveFirm } = useContext(AppBarContext)
    const firmId = currentUserActiveFirm?.firmId
    const { isCurrentlyOnOMWorkspace } = useOMWorkspace()
    const showAuditTips =
        isOMBestPracticeFeatureEnabled() && isCurrentlyOnOMWorkspace

    const [isProjectParameterModeOn, setIsProjectParameterModeOn] = useState(
        false
    )
    const [isGlobalBPModeOn, setIsGlobalBPModeOn] = useState(false)
    const [parameterToggles, setParameterToggles] = useState<
        ParameterToggleState
    >(emptyParameterToggles)
    const [parameterSelected, setParameterSelected] = useState<
        ParameterSelectedState
    >(emptyParameterSelected)

    const { data: firmData } = useQuery<FirmDataShape>(
        FirmProjectParametersQuery,
        {
            variables: { firmId: firmId || '' },
            skip: !firmId,
        }
    )

    const firmOptions = useMemo(() => buildFirmBPParameters(firmData).options, [
        firmData,
    ])

    // Mirror local state into the staged-config reactive var consumed by
    // useApplyBPFromConfig.
    useEffect(() => {
        const mode: BPMode = isGlobalBPModeOn
            ? BP_MODE.GLOBAL
            : isProjectParameterModeOn
            ? BP_MODE.PARAMETER
            : BP_MODE.NONE

        const effectiveSelected =
            mode === BP_MODE.PARAMETER
                ? buildEffectiveSelected(parameterToggles, parameterSelected)
                : emptyParameterSelected

        const projectGroup =
            mode === BP_MODE.PARAMETER &&
            parameterToggles.projectGroup &&
            parameterSelected.projectGroup.length > 0
                ? parameterSelected.projectGroup[0]
                : undefined

        setBPStagedConfig({
            mode,
            projectGroup,
            parameterSelected: effectiveSelected,
        })
    }, [
        isGlobalBPModeOn,
        isProjectParameterModeOn,
        parameterToggles,
        parameterSelected,
    ])

    const handleProjectParameterToggle = (checked: boolean) => {
        setIsProjectParameterModeOn(checked)
        if (checked) {
            setIsGlobalBPModeOn(false)
            setParameterToggles((prev) => {
                const updated = { ...prev }
                ;(Object.keys(updated) as ParameterKey[]).forEach((key) => {
                    if (parameterSelected[key].length > 0) updated[key] = true
                })
                return updated
            })
        } else {
            setParameterToggles(emptyParameterToggles)
        }
    }

    const handleGlobalBPToggle = (checked: boolean) => {
        setIsGlobalBPModeOn(checked)
        if (checked) {
            setIsProjectParameterModeOn(false)
            setParameterToggles(emptyParameterToggles)
        }
    }

    const handleParameterToggle = (key: ParameterKey) => {
        setParameterToggles((prev) => {
            const newChecked = !prev[key]
            if (newChecked) {
                setIsProjectParameterModeOn(true)
                setIsGlobalBPModeOn(false)
                // Default to first option so the user isn't forced into an
                // explicit "Select..." step.
                setParameterSelected((prevSelected) => {
                    if (prevSelected[key].length > 0) return prevSelected
                    const firstOption = firmOptions[key]?.[0]
                    if (!firstOption) return prevSelected
                    return { ...prevSelected, [key]: [firstOption] }
                })
            }
            return { ...prev, [key]: newChecked }
        })
    }

    const handleParameterSelectedChange = (
        key: ParameterKey,
        values: string[]
    ) => {
        setParameterSelected((prev) => ({ ...prev, [key]: values }))
        if (values.length === 0) {
            setParameterToggles((prev) => ({ ...prev, [key]: false }))
        }
    }

    return (
        <div className={classes.contentRoot}>
            {showAuditTips && (
                <>
                    <div className={classes.auditTipsScrollContainer}>
                        <BestPracticeTipsList />
                    </div>
                    <div className={classes.divider} />
                </>
            )}

            <Typography className={classes.headerText}>
                You can apply the selected Best Practice parameters to any
                content by <b>double-clicking</b> it on the left.
            </Typography>

            <FormControlLabel
                className={classes.parameterRow}
                classes={{ root: classes.rowMarginLeftZero }}
                checked={isProjectParameterModeOn}
                control={<Switch color="primary" size="small" />}
                label={
                    <span className={classes.sectionTitle}>
                        Set as Project Parameter Best Practice
                    </span>
                }
                labelPlacement="start"
                onChange={(_e, checked) =>
                    handleProjectParameterToggle(checked)
                }
            />

            <div className={classes.divider} />

            <ParameterListSection
                firmOptions={firmOptions}
                parameterToggles={parameterToggles}
                parameterSelected={parameterSelected}
                onToggle={handleParameterToggle}
                onSelectedChange={handleParameterSelectedChange}
            />

            <div className={classes.divider} />

            <FormControlLabel
                className={classes.parameterRow}
                classes={{ root: classes.rowMarginLeftZero }}
                checked={isGlobalBPModeOn}
                control={<Switch color="primary" size="small" />}
                label={
                    <span className={classes.sectionTitle}>
                        Set as Global Best Practice
                    </span>
                }
                labelPlacement="start"
                onChange={(_e, checked) => handleGlobalBPToggle(checked)}
            />
        </div>
    )
}
